import React from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box } from '@mui/material';
import { Download } from '@mui/icons-material';
import { CSVData } from '../utils/csvParser';
import { downloadService } from '../services/downloadService';

interface ClientSummaryTableProps {
  data: CSVData[];
  fontFamily?: string;
}

export const ClientSummaryTable: React.FC<ClientSummaryTableProps> = ({ data, fontFamily = 'JetBrains Mono' }) => {
  const formatNumber = (num: number) => num.toLocaleString('en-IN');

  // Calculate totals
  const totalSuccessful = data.reduce((sum, row) => sum + (Number(row.successful_requests) || 0), 0);
  const totalDataWithoutMapping = data.reduce((sum, row) => sum + (Number(row.data_without_mapping_requests) || 0), 0);
  const totalFailed = data.reduce((sum, row) => sum + (Number(row.failed_requests) || 0), 0);
  const totalRequests = data.reduce((sum, row) => sum + (Number(row.total_requests) || 0), 0);

  const handleDownload = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    downloadService.downloadTableAsCSV(data, `client_api_summary_${timestamp}.csv`);
  };
  
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3, fontFamily }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily }}>
          📋 Client API Summary
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownload}
          size="small"
          sx={{ fontFamily }}
        >
          Download CSV
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Client Name</TableCell>
              <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Sub Product</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Successful Requests</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Data Without Mapping</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Failed Requests</TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>Total Requests</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ fontFamily }}>No data available</TableCell>
              </TableRow>
            ) : (
              <>
                {data.map((row, index) => {
                  const isChallan = String(row.sub_product).toLowerCase().includes('challan');
                  return (
                  <TableRow key={index} hover>
                    <TableCell sx={{ fontFamily }}>{row.client_name}</TableCell>
                    <TableCell sx={{ fontFamily }}>{row.sub_product}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.successful_requests) || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{!isChallan ? formatNumber(Number(row.data_without_mapping_requests) || 0) : 'NA'}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.failed_requests) || 0)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily }}>{formatNumber(Number(row.total_requests) || 0)}</TableCell>
                  </TableRow>
                )})}
                {/* Total Row */}
                <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold' }}>
                  <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>Total</TableCell>
                  <TableCell sx={{ fontFamily, fontWeight: 'bold' }}>All</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalSuccessful)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalDataWithoutMapping)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalFailed)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily, fontWeight: 'bold' }}>{formatNumber(totalRequests)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}; 